import useOffClick from '@flight-workspace/common-client/src/hooks/useOffClick';
import classNames from 'classnames/bind';
import { POPUP_AD_ROOT_ELEMENT_CLASS_NAME } from 'common/PopupAd';
import ChartDomestic from 'common/modal/chart/domestic';
import ChartOversea from 'common/modal/chart/oversea';
import { DateFormat, IKeyAnyValue } from 'common/types';
import { days } from 'common/utils';
import { IInfoCity, useInfoParamsContext } from 'contexts/infoParams';
import { IModalComponent, Modal, ModalPosition } from 'contexts/modalFunc';
import { SeatClass, TripType } from 'contexts/searchParams';
import { IPassenger } from 'hooks/useHistory';
import Footer from '../Footer';
import Calendar from '../calendar';
import RecommendProduct from '../recommend';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

const Modals: IKeyAnyValue = {
  [Modal.CALENDAR]: ({
    payload: { index } = { index: undefined },
  }: IKeyAnyValue) => <Calendar index={index} />,
  [Modal.FARE_CHART]: ({ payload }: { payload: any }) => {
    return payload?.isDomestic ? (
      <ChartDomestic payload={payload} />
    ) : (
      <ChartOversea payload={payload} />
    );
  },
  [Modal.RECOMMEND]: () => <RecommendProduct />,
};

const getChartPayloadForOverseas = (
  isPopular: boolean,
  tripType: TripType,
  itinerary: any,
  passengers: IPassenger,
  seatClass: SeatClass,
) => {
  // 메인 인기여행지 해외 선택시
  if (isPopular)
    return {
      isPopular: true,
      tripType: TripType.RT,
      passengers,
      seatClass,
      isDomestic: false,
      departureCity: itinerary[0]?.departureCity,
      arrivalCity: itinerary[0]?.arrivalCity,
    };
  // 메인 서치박스에서 해외 선택시
  return {};
};

// 국내용
const getChartPayloadForDomestic = (
  isPopular: Boolean,
  departureCity: IInfoCity,
  arrivalCity: IInfoCity,
  departureDate: string,
  period: number,
  itinerary: any,
  tripType: TripType,
  passengers: IPassenger,
  seatClass: SeatClass,
  // payload?: IKeyAnyValue | undefined,
) => {
  const now = days().format(DateFormat.YMD);
  // 메인 인기여행지가 국내
  if (isPopular) {
    return {
      isPopular: true,
      departureCity,
      arrivalCity,
      tourDepartureDate: departureDate || now,
      tourReturnDate:
        departureDate && period
          ? days(departureDate)
              .add(period - 1, 'day')
              .format(DateFormat.YMD)
          : now,
      tripType,
      passengers,
      seatClass,
      isDomestic: true,
    };
  }
  // 서치박스 국내일때
  return {
    departureCity: itinerary[0]?.departureCity,
    arrivalCity: itinerary[0]?.arrivalCity,
    tourDepartureDate: itinerary[0]?.departureDate || now,
    tourReturnDate:
      itinerary[1]?.departureDate || itinerary[0]?.departureDate || now,
    tripType,
    passengers,
    seatClass,
    isDomestic: true,
  };
};

const SearchModalContainer = ({
  modalInfo: { type, position, payload },
  searchParams: { itinerary, tripType, passengers, seatClass },
  setModal,
  isSearchBoxFixed,
}: IModalComponent) => {
  const ModalComponent = Modals[type];
  if (!ModalComponent) {
    return null;
  }

  const {
    infoSearchParams: { departureCity, arrivalCity, departureDate, period },
  } = useInfoParamsContext();

  const modalHandler =
    (_type: Modal, _position = ModalPosition.MAIN, _payload?: IKeyAnyValue) =>
    (e: MouseEvent) => {
      if (
        (e.target as HTMLElement)?.closest(
          `.${POPUP_AD_ROOT_ELEMENT_CLASS_NAME}`,
        )
      ) {
        return;
      }
      setModal({ type: _type, payload: _payload, position: _position });
    };
  const closeModal = modalHandler(Modal.NULL);

  const ref = useOffClick(closeModal);

  const isNotMulti =
    tripType !== TripType.MD && !!itinerary[0]?.arrivalCity?.name;

  let onlyInternetional = false;
  let onlyDomestic = false;
  // 인기여행지일때
  if (payload?.isPopular) {
    onlyInternetional = !payload?.isDomestic;
    onlyDomestic = !!payload?.isDomestic;
    // 인기여행지외
  } else {
    onlyInternetional =
      !!itinerary[0].departureCity?.isDomestic &&
      !itinerary[0].arrivalCity?.isDomestic;
    onlyDomestic =
      !!itinerary[0].departureCity?.isDomestic &&
      !!itinerary[0].arrivalCity?.isDomestic;
  }

  return (
    <div
      className={cx('SearchModalContainer')}
      role="dialog"
      aria-modal="true"
      data-modalOnlyMobile="true"
      ref={ref}
      data-fixed={isSearchBoxFixed}
    >
      <div className={cx('header')}>
        <div className={cx('tablist')} role="tablist">
          {position !== ModalPosition.RECOMMEND && (
            <button
              type="button"
              className={cx('tab', 'as_calendar')}
              role="tab"
              aria-selected={type === Modal.CALENDAR}
              data-event-nlog="click"
              data-event-area-code="cal.cal"
              onClick={e =>
                modalHandler(
                  Modal.CALENDAR,
                  position,
                )(e as unknown as MouseEvent)
              }
            >
              캘린더
            </button>
          )}

          {/* 국내: 편도,왕복, 해외: 왕복 */}
          {((isNotMulti && (onlyInternetional || onlyDomestic)) ||
            position === ModalPosition.RECOMMEND) &&
            tripType !== TripType.EVERYWHERE && (
              <>
                <button
                  type="button"
                  className={cx('tab', 'as_chart')}
                  role="tab"
                  aria-selected={type === Modal.FARE_CHART}
                  data-event-nlog="click"
                  data-event-area-code="cal.graph"
                  onClick={e =>
                    modalHandler(
                      Modal.FARE_CHART,
                      position,
                      onlyDomestic
                        ? getChartPayloadForDomestic(
                            !!payload?.isPopular,
                            departureCity,
                            arrivalCity,
                            departureDate,
                            period,
                            itinerary,
                            tripType,
                            passengers,
                            seatClass,
                          )
                        : getChartPayloadForOverseas(
                            !!payload?.isPopular,
                            tripType,
                            itinerary,
                            passengers,
                            seatClass,
                          ),
                    )(e as unknown as MouseEvent)
                  }
                >
                  가격그래프
                </button>
                <button
                  type="button"
                  className={cx('tab', 'as_recommend')}
                  role="tab"
                  aria-selected={type === Modal.RECOMMEND}
                  data-event-nlog="click"
                  data-event-area-code="cal.recommend"
                  onClick={e =>
                    modalHandler(Modal.RECOMMEND, position, {
                      isPopular: !!payload?.isPopular,
                      isDomestic: !!payload?.isDomestic,
                    })(e as unknown as MouseEvent)
                  }
                >
                  추천일정
                </button>
              </>
            )}
        </div>
        <button
          type="button"
          className={cx('close')}
          data-event-nlog="click"
          data-event-area-code="cal.close"
          onClick={e => closeModal(e as unknown as MouseEvent)}
        />
      </div>
      <div
        className={cx('content', {
          as_calendar: type === Modal.CALENDAR,
          as_chart: type === Modal.FARE_CHART,
          as_recommend: type === Modal.RECOMMEND,
        })}
      >
        <ModalComponent payload={payload} />
        <div className={cx('footer')}>
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default SearchModalContainer;
